import {
  INodeCardProps,
  OwnerType,
  TForbidenCountryLegalEntity,
} from '~/modules/identification/forms/OwnershipStructure/types.ts';
import { Typography, useTheme } from '@mui/material';
import { styles } from '../../styles.ts';
import {
  getCountry,
  renderIndividualStatus,
} from '~/modules/identification/forms/OwnershipStructure/utils/diagramUtils.tsx';

export const NodeCardContent = ({ data }: INodeCardProps) => {
  const theme = useTheme();
  const {
    ownerType,
    fullName,
    birthDate,
    citizenshipCountry,
    address,
    identificationNumber,
    foreignTaxNumber,
    isBeneficiary,
    sourceURL,
    isRoot,
    rangeIndividual,
    rangeLegal,
    hasRussianOrBelarusianCitizenShareholders,
    russianBelarusianIndividuals,
    russianBelarusianLegalEntities,
    areIndividualsWithDecisiveInfluenceAbsent,
    isDuplicate,
  } = data;

  const individualsText =
    russianBelarusianIndividuals?.map(
      (item) =>
        `${item.fullName} — ${renderIndividualStatus(item)} з часткою ${item.ownershipShare}%`
    ) ?? [];

  const legalEntitiesText =
    russianBelarusianLegalEntities?.map((item: TForbidenCountryLegalEntity) => {
      const country = getCountry(item.residencyStatus?.value);
      return country
        ? `${item.entityName} — резидент ${country} з часткою ${item.ownershipShare}%`
        : `${item.entityName} з часткою ${item.ownershipShare}%`;
    }) ?? [];

  const allItems = [...individualsText, ...legalEntitiesText];

  const registrationCountry = address?.registrationAddress?.country?.label;

  switch (ownerType as OwnerType) {
    case OwnerType.INDIVIDUAL:
      return (
        <>
          {isBeneficiary && (
            <Typography sx={{ ...styles.textCard, color: theme.palette.primary.main }}>
              КБВ
            </Typography>
          )}
          <Typography sx={styles.textCard}>ПІБ: {fullName}</Typography>
          {birthDate && <Typography sx={styles.textCard}>Дата народження: {birthDate}</Typography>}
          {(identificationNumber || foreignTaxNumber) && (
            <Typography sx={styles.textCard}>
              РНОКПП або код для нерезидентів: {identificationNumber || foreignTaxNumber}
            </Typography>
          )}
          {citizenshipCountry && (
            <Typography sx={styles.textCard}>
              Країна громадянства: {citizenshipCountry.label}
            </Typography>
          )}
          {address?.permanentAddress?.country && (
            <Typography sx={styles.textCard}>
              Країна проживання: {address.permanentAddress.country.label}
            </Typography>
          )}
        </>
      );

    case OwnerType.LEGAL_ENTITY:
      return (
        <>
          <Typography sx={styles.textCard}>
            {isRoot ? fullName : `Найменування: ${fullName}`}
          </Typography>
          {identificationNumber ? (
            <Typography sx={styles.textCard}>ЄДРПОУ: {identificationNumber}</Typography>
          ) : foreignTaxNumber && identificationNumber ? (
            <Typography sx={styles.textCard}>ЄДРПОУ: {identificationNumber}</Typography>
          ) : foreignTaxNumber ? (
            <Typography sx={styles.textCard}>Реєстраційний номер: {foreignTaxNumber}</Typography>
          ) : (
            ''
          )}
          {registrationCountry && !isRoot && (
            <Typography sx={styles.textCard}>Країна реєстрації: {registrationCountry}</Typography>
          )}
          {areIndividualsWithDecisiveInfluenceAbsent && (
            <Typography sx={styles.textCard}>
              Відсутні фізичні особи, які мають можливість здійснювати вирішальний вплив на
              управління або господарську діяльність.
            </Typography>
          )}
          {isDuplicate && (
            <Typography sx={{ ...styles.textCard, color: theme.palette.primary.main }}>
              Учасник повторюється у структурі
            </Typography>
          )}
        </>
      );

    case OwnerType.FREE_FLOAT:
      return (
        <>
          <Typography sx={styles.textCard}>{fullName}</Typography>
          {sourceURL && <Typography sx={styles.sourceURL}>Джерело: {sourceURL}</Typography>}
        </>
      );

    case OwnerType.SHAREHOLDER_GROUP:
      return (
        <>
          <Typography sx={styles.textCard}>{fullName}</Typography>
          {rangeLegal && (
            <Typography sx={styles.textCard}>
              {rangeLegal.number} ЮО із частки ЮО (%): {rangeLegal.from} - {rangeLegal.to}
            </Typography>
          )}
          {rangeIndividual && (
            <Typography sx={styles.textCard}>
              {rangeIndividual.number} ФО із частки ФО (%): {rangeIndividual.from} -{' '}
              {rangeIndividual.to}
            </Typography>
          )}
          {!hasRussianOrBelarusianCitizenShareholders ? (
            <Typography sx={styles.textCard}>
              Жодна із зазначених ФО/ЮО не є громадянами/резидентами РФ та РБ.
            </Typography>
          ) : (
            <>
              {individualsText.map((text, index) => {
                const isLast = index === allItems.length - 1;
                const punctuation = isLast ? '.' : ',';
                return (
                  <Typography key={`individual-${index}`} sx={styles.textCard}>
                    {index === 0 ? `окрім ${text}${punctuation}` : `${text}${punctuation}`}
                  </Typography>
                );
              })}
              {legalEntitiesText.map((text, index) => {
                const globalIndex = individualsText.length + index;
                const isLast = globalIndex === allItems.length - 1;
                const punctuation = isLast ? '.' : ',';
                return (
                  <Typography key={`legal-${index}`} sx={styles.textCard}>
                    {globalIndex === 0 ? `окрім ${text}${punctuation}` : `${text}${punctuation}`}
                  </Typography>
                );
              })}
            </>
          )}
        </>
      );

    case OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP:
      return (
        <>
          {isBeneficiary && (
            <Typography sx={{ ...styles.textCard, color: theme.palette.primary.main }}>
              КБВ незалежно від формального володіння
            </Typography>
          )}
          <Typography sx={styles.textCard}>ПІБ: {fullName}</Typography>
          {birthDate && <Typography sx={styles.textCard}>Дата народження: {birthDate}</Typography>}
          {(identificationNumber || foreignTaxNumber) && (
            <Typography sx={styles.textCard}>
              РНОКПП або код для нерезидентів: {identificationNumber || foreignTaxNumber}
            </Typography>
          )}
          {citizenshipCountry && (
            <Typography sx={styles.textCard}>
              Країна громадянства: {citizenshipCountry.label}
            </Typography>
          )}
          {address?.permanentAddress?.country && (
            <Typography sx={styles.textCard}>
              Країна проживання: {address.permanentAddress.country.label}
            </Typography>
          )}
        </>
      );

    default:
      return null;
  }
};
